/**
 * Janelas de comparação da Home.
 *
 * O bridge (`/vendas/comparacao`) aceita as quatro datas
 * (`data_inicio_principal`, `data_fim_principal`, `data_inicio_comparacao`,
 * `data_fim_comparacao`). Quando o front não manda as duas últimas, o backend
 * assume o período imediatamente anterior — que era o único modo disponível
 * até aqui. Este módulo calcula as outras janelas para mandar explicitamente.
 *
 * Tudo em `YYYY-MM-DD`, com aritmética em UTC: o corte da base é por data
 * civil (`reference_date`), não por instante, então converter para Date local
 * introduziria erro de um dia perto da meia-noite.
 */

export type ModoComparacao = 'anterior' | 'mes_passado' | 'ano_passado'

export type Janela = { inicio: string; fim: string }

export const MODOS_COMPARACAO: Array<{ id: ModoComparacao; label: string; hint: string }> = [
  {
    id: 'anterior',
    label: 'Período anterior',
    hint: 'Os mesmos dias imediatamente antes do período escolhido',
  },
  {
    id: 'mes_passado',
    label: 'Mesmo período do mês passado',
    hint: 'O mesmo intervalo de dias, um mês atrás',
  },
  {
    id: 'ano_passado',
    label: 'Mesmo período do ano passado',
    hint: 'O mesmo intervalo de dias, um ano atrás',
  },
]

export function labelModo(modo: ModoComparacao): string {
  return MODOS_COMPARACAO.find((m) => m.id === modo)?.label ?? 'Período anterior'
}

/** `YYYY-MM-DD` → partes numéricas. Devolve null se não for uma data válida. */
function parseIso(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || '').trim())
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = Number(match[3])
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  return { y, m, d }
}

function toIso(y: number, m: number, d: number): string {
  const mm = String(m).padStart(2, '0')
  const dd = String(d).padStart(2, '0')
  return `${y}-${mm}-${dd}`
}

/** Último dia do mês (1-12), já com bissexto. */
export function ultimoDiaDoMes(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

/**
 * Desloca uma data em meses, mantendo o dia quando ele existe no mês destino.
 * 31/03 − 1 mês = 28/02 (ou 29/02) — sem "vazar" para o mês seguinte, que é o
 * que `setMonth` faria.
 */
function deslocarMeses(iso: string, meses: number): string | null {
  const p = parseIso(iso)
  if (!p) return null
  const totalMes = p.y * 12 + (p.m - 1) + meses
  const y = Math.floor(totalMes / 12)
  const m = (totalMes % 12) + 1
  const d = Math.min(p.d, ultimoDiaDoMes(y, m))
  return toIso(y, m, d)
}

/** Dias entre duas datas, inclusivo nas duas pontas. */
export function duracaoEmDias(inicio: string, fim: string): number | null {
  const a = parseIso(inicio)
  const b = parseIso(fim)
  if (!a || !b) return null
  const ms = Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)
  return Math.floor(ms / 86_400_000) + 1
}

function somarDias(iso: string, dias: number): string | null {
  const p = parseIso(iso)
  if (!p) return null
  const t = new Date(Date.UTC(p.y, p.m - 1, p.d))
  t.setUTCDate(t.getUTCDate() + dias)
  return toIso(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate())
}

/**
 * A janela com que o período escolhido será comparado.
 *
 * `anterior` reproduz a conta do backend (mesma duração, colada antes) — o
 * front manda explícito para a tela poder rotular a janela sem adivinhar.
 * Devolve null se as datas de entrada não forem válidas.
 */
export function janelaComparacao(
  inicio: string,
  fim: string,
  modo: ModoComparacao,
): Janela | null {
  if (!parseIso(inicio) || !parseIso(fim)) return null

  if (modo === 'anterior') {
    const dias = duracaoEmDias(inicio, fim)
    if (dias == null || dias < 1) return null
    const compFim = somarDias(inicio, -1)
    const compInicio = compFim ? somarDias(compFim, -(dias - 1)) : null
    return compInicio && compFim ? { inicio: compInicio, fim: compFim } : null
  }

  const passo = modo === 'mes_passado' ? -1 : -12
  const compInicio = deslocarMeses(inicio, passo)
  const compFim = deslocarMeses(fim, passo)
  return compInicio && compFim ? { inicio: compInicio, fim: compFim } : null
}

/** Rótulo curto da janela, para o subtítulo do gráfico: "17/07 → 15/08". */
export function rotularJanela(j: Janela | null): string {
  if (!j) return '—'
  const fmt = (iso: string) => {
    const p = parseIso(iso)
    return p ? `${String(p.d).padStart(2, '0')}/${String(p.m).padStart(2, '0')}` : iso
  }
  return `${fmt(j.inicio)} → ${fmt(j.fim)}`
}

/**
 * MC % ponderada pela receita — `Σ margem ÷ Σ receita`.
 *
 * É a régua do painel "Margem de contribuição por anúncio" e a da skill
 * `fiesta-base-dados`: nunca a média aritmética dos percentuais linha a linha,
 * que dá peso igual a um pedido de R$ 12 e a um de R$ 12.000.
 *
 * Devolve null quando não há receita — 0% seria uma afirmação que o dado não
 * sustenta.
 */
export function mcPonderada(margemTotal: number, receita: number): number | null {
  if (!Number.isFinite(margemTotal) || !Number.isFinite(receita)) return null
  if (receita <= 0) return null
  return (margemTotal / receita) * 100
}

/** Variação percentual entre dois valores. null quando não há base de comparação. */
export function variacaoPct(atual: number, anterior: number | null | undefined): number | null {
  if (anterior == null || !Number.isFinite(anterior) || anterior === 0) return null
  if (!Number.isFinite(atual)) return null
  return ((atual - anterior) / Math.abs(anterior)) * 100
}
